import {
  ActivityDetails as ApiActivityDetails,
  ActivityRow as ApiActivityRow,
  ActivitySentMessage,
  ApiError,
  app__services__Automations__messages__automations__PersonRef as ApiPerson,
  AutomationCard,
  AutomationResponse,
  AutomationSave,
  AutomationsService,
  AutomationWhen,
  CardAnswer,
  CardOutcome,
  ConnectedApp as ApiConnectedApp,
  StepDraft,
  StepResponse,
  TaskStatus,
} from "client/openapi";
import type { FieldValue } from "components/FieldValueInput";
import { errorText } from "components/Forms/formsApi";
import { namesInWords } from "components/MessageTemplates/TemplatePreviewParts";
import type { AutomationsApi } from "./api";
import {
  aboutRoles,
  BACKEND_ACTIONS,
  defaultTimingValue,
  isTaskKind,
  kindTiming,
  TASK_FINISHED,
  TASK_STATUS_CHANGED,
} from "./kinds";
import { laneColumns } from "./AutomationsTabModel";
import {
  cardTaskStep,
  entryCards,
  fitToTrigger,
  isLaneTask,
  isSubjects,
  Lane,
  offsetMs,
  RIGHT_AWAY,
  TASK_STEP,
} from "./model";
import {
  columnInfo,
  fieldByRef,
  ReferenceData,
  nameOf,
  ROLE_OF_ACCOUNT_ROLE,
} from "./reference";
import type {
  ActivityAction,
  ActivityDetails,
  ActivityFilter,
  ActivityPage,
  ActivityPillKind,
  ActivityQuery,
  ActivityRow,
  AnswerResult,
  AppName,
  ApprovalAnswer,
  ApprovalIds,
  ApprovalsQuery,
  ApproveAllResult,
  ApprovalFilter,
  ApprovalItem,
  Approvers,
  AssignChoice,
  AssignTo,
  Automation,
  AutomationDraft,
  AutomationPatch,
  ConnectedApp,
  ConnectedApps,
  ClosedTask,
  CloseStatus,
  CrmValue,
  Gate,
  PersonKey,
  Position,
  Proposal,
  ProposalEdits,
  SendTo,
  SkipKind,
  Step,
  StepSample,
  StepTally,
  StudentTutors,
  TaskChange,
  TimePosition,
  Trigger,
  TriggerCard,
  TriggerFilter,
  TriggerJoin,
  TurnOn,
  TurnOnPeople,
  TurnOnPreview,
  TurnOnTarget,
  UpcomingItem,
} from "./types";
import { fieldValueWords, subjectNames, suggestName } from "./words";

/*
 * The AutomationsApi over the backend's /automations routes (the generated
 * client's AutomationsService). It turns the backend's automations (an
 * entry of one or more trigger cards, then steps in stages, one stage per
 * column, a gate's column opened by its wait) into the screens' (the
 * trigger and its cards, then steps at times, each in its stage, and the
 * gates between them) and back,
 * and the backend's cards, Activity rows and connected apps into the
 * screens' shapes. Saving sends the whole automation, merged into the last
 * one read; anything that ends up on is previewed first, and the save
 * carries the preview's counts (seen). The only writes are the routes'
 * own. After every change it tells its subscribers, and again when the
 * page comes back into view.
 */

const TASK_STATUS = "task_status";
const WAIT = StepDraft.action.WAIT;
const HOUR = 60;
const DAY = 24 * HOUR;
/** Activity's longest list (the route's own limit). */
const ACTIVITY_LIMIT = 200;
/** How many items one request of Approve all answers, so none is held open for long. */
const APPROVE_BATCH = 25;
/** Automation names (the route's own limit). */
const NAME_LENGTH = 100;

// ── People ────────────────────────────────────────────────────────────────

/**
 * A backend key as the screens' key: "account:<reference id>" is an admin
 * (or, for a tutor's own login, that tutor); students, parents and tutors
 * keep theirs.
 */
function personFrom(
  ref: ReferenceData,
  key: string | null | undefined
): PersonKey | null {
  if (!key) return null;
  const at = key.indexOf(":");
  const kind = key.slice(0, at);
  const id = key.slice(at + 1);
  if (kind !== "account") return key as PersonKey;
  const admin: PersonKey = `admin:${id}`;
  if (ref.personByKey[admin]) return admin;
  return ref.tutorOfAccount[id] ?? admin;
}

/** A screens' key as the backend's. */
function personTo(key: PersonKey): string {
  return key.startsWith("admin:") ? `account:${key.slice(6)}` : key;
}

/** A person the backend named, as a key; their name is kept for people this page didn't load. */
function named(
  ref: ReferenceData,
  person: ApiPerson | null | undefined
): PersonKey | null {
  const key = personFrom(ref, person?.key);
  if (key && person?.name && !ref.personByKey[key])
    ref.names[key] = person.name;
  return key;
}

function namedAll(ref: ReferenceData, people: ApiPerson[] | null | undefined) {
  return (people ?? [])
    .map((person) => named(ref, person))
    .filter((key): key is PersonKey => key !== null);
}

/** Keys with names beside them (a card's task), as the screens' keys. */
function keysWithNames(
  ref: ReferenceData,
  keys: string[] | null | undefined,
  names: string[] | null | undefined
) {
  return (keys ?? [])
    .map((key, i) => named(ref, { key, name: names?.[i] ?? "" }))
    .filter((key): key is PersonKey => key !== null);
}

// ── Who can approve ───────────────────────────────────────────────────────

const APPROVERS_IN: Record<string, Approvers> = {
  admin: "admins",
  tutor: "tutors",
  both: "both",
};

const APPROVERS_OUT: Record<Approvers, StepDraft.approver_roles> = {
  admins: StepDraft.approver_roles.ADMIN,
  tutors: StepDraft.approver_roles.TUTOR,
  both: StepDraft.approver_roles.BOTH,
};

// ── The trigger ───────────────────────────────────────────────────────────

/**
 * The backend's When as the screens' trigger. A task's status change is
 * "A task is finished" when its status is Complete, else "A task's status
 * changes" (no status: any change); its options are the filters.
 */
function triggerOf(
  ref: ReferenceData,
  when: { kind: string; options?: any }
): Trigger {
  const o: Record<string, any> = when.options ?? {};
  if (when.kind !== TASK_STATUS)
    return { kind: when.kind, options: { ...o }, filters: [] };
  const filters: TriggerFilter[] = [];
  if (o.name_contains)
    filters.push({ field: "name_contains", value: String(o.name_contains) });
  if (o.made_by !== undefined && o.made_by !== null && o.made_by !== "")
    filters.push({ field: "made_by", value: String(o.made_by) });
  if (o.assigned_to)
    filters.push({
      field: "assigned_to",
      value: personFrom(ref, o.assigned_to),
    });
  if (o.about)
    filters.push({ field: "about", value: personFrom(ref, o.about) });
  return o.status === TaskStatus.COMPLETE
    ? { kind: TASK_FINISHED, options: {}, filters }
    : {
        kind: TASK_STATUS_CHANGED,
        options: { to: o.status || "any" },
        filters,
      };
}

/** The screens' trigger as the backend's When (one of each task filter). */
function whenOf(trigger: Trigger): AutomationWhen {
  if (!isTaskKind(trigger.kind))
    return { kind: trigger.kind, options: { ...(trigger.options ?? {}) } };
  const to = trigger.options?.to;
  const options: Record<string, unknown> = {
    status:
      trigger.kind === TASK_FINISHED
        ? TaskStatus.COMPLETE
        : to && to !== "any"
        ? to
        : null,
  };
  trigger.filters.forEach((filter) => {
    if (filter.field in options) return;
    switch (filter.field) {
      case "name_contains":
        if (filter.value.trim()) options.name_contains = filter.value.trim();
        return;
      case "made_by":
        options.made_by =
          !filter.value || filter.value === "any"
            ? "any"
            : Number(filter.value);
        return;
      case "assigned_to":
      case "about":
        if (filter.value) options[filter.field] = personTo(filter.value);
    }
  });
  return { kind: TASK_STATUS, options };
}

// ── Positions ─────────────────────────────────────────────────────────────

/** Minutes from the start of a stage as a time: before when negative; whole days in days, else hours. */
function timeOf(minutes: number): TimePosition {
  if (!minutes) return { ...RIGHT_AWAY };
  const size = Math.abs(minutes);
  const side = minutes < 0 ? { before: true } : {};
  return size % DAY === 0
    ? { kind: "time", n: size / DAY, unit: "days", ...side }
    : { kind: "time", n: size / HOUR, unit: "hours", ...side };
}

function minutesOf(position: Position) {
  return Math.round(offsetMs(position) / 60000);
}

/** A card's own timing (offset_minutes) as a time; null for none. */
function cardTimeOf(minutes: number | null | undefined) {
  return minutes === null || minutes === undefined ? null : timeOf(minutes);
}

/**
 * A gate's condition as its card: being in a CRM column is a Joins a CRM
 * column card, a task from the lane having a status is a task card naming
 * its step, and a kind card is that kind with its own timing.
 */
function gateCardOf(ref: ReferenceData, condition: any): TriggerCard {
  if (condition?.wait === "column")
    return {
      id: null,
      trigger: {
        kind: "column_joined",
        options: { column: condition.column ?? null },
        filters: [],
      },
      time: null,
    };
  if (condition?.wait === "task_status") {
    const step =
      condition.step !== undefined && condition.step !== null
        ? String(condition.step)
        : null;
    const status: string = condition.status || TaskStatus.COMPLETE;
    return {
      id: null,
      trigger:
        status === TaskStatus.COMPLETE
          ? { kind: TASK_FINISHED, options: { [TASK_STEP]: step }, filters: [] }
          : {
              kind: TASK_STATUS_CHANGED,
              options: { [TASK_STEP]: step, to: status },
              filters: [],
            },
      time: null,
    };
  }
  return {
    id: typeof condition?.card === "number" ? condition.card : null,
    trigger: triggerOf(ref, {
      kind: condition?.kind ?? "",
      options: condition?.options ?? {},
    }),
    time: cardTimeOf(condition?.offset_minutes),
  };
}

/** A card's trigger as the backend's kind and options, without the kind's own timing. */
function cardWhen(ref: ReferenceData, trigger: Trigger): AutomationWhen {
  const when = whenOf(trigger);
  const timing = kindTiming(ref, when.kind);
  const options = { ...(when.options ?? {}) };
  if (timing) delete options[timing.key];
  return { kind: when.kind, options };
}

/** A card's own timing in minutes, as a first column of its kind counts them; null for a kind without one. */
function cardMinutes(ref: ReferenceData, card: TriggerCard) {
  const timing = kindTiming(ref, card.trigger.kind);
  if (!timing) return null;
  return card.time
    ? minutesOf(card.time)
    : defaultTimingValue(ref, card.trigger.kind) *
        (timing.key === "hours" ? HOUR : DAY);
}

/** A gate's card as its condition; a task by its step's id, or a new step's ref. */
function conditionOf(
  ref: ReferenceData,
  card: TriggerCard,
  stepRef: (id: string | null) => number | string | null
) {
  const { trigger } = card;
  if (isLaneTask(trigger))
    return {
      wait: "task_status",
      step: stepRef(cardTaskStep(trigger)),
      status:
        trigger.kind === TASK_FINISHED
          ? TaskStatus.COMPLETE
          : trigger.options?.to ?? TaskStatus.CANCELLED,
    };
  if (trigger.kind === "column_joined")
    return { wait: "column", column: trigger.options?.column ?? null };
  const when = cardWhen(ref, trigger);
  return {
    wait: "trigger",
    card: card.id,
    kind: when.kind,
    options: when.options,
    offset_minutes: cardMinutes(ref, card),
  };
}

// ── Assign to ─────────────────────────────────────────────────────────────

/**
 * The screens' Assign to choices as the backend's: "The person it is
 * about" is the student (the tutor for an event about tutors, the parent
 * for one about parents), "From the event" is who finished the task or
 * filled out the form, and All tutors is everyone teaching the subject
 * (no subject: every active tutor).
 */
function choicesTo(
  ref: ReferenceData,
  trigger: Trigger,
  choices: AssignChoice[]
) {
  const roles = aboutRoles(ref, trigger);
  const only = roles.length === 1 ? roles[0] : null;
  const out = choices.map((choice) => {
    switch (choice) {
      case "self":
        return only === "tutor"
          ? "tutor_self"
          : only === "parent"
          ? "parents"
          : "student";
      case "event_person":
        return isTaskKind(trigger.kind) ? "finisher" : "filler";
      case "all_tutors":
        return "teach";
      default:
        return choice;
    }
  });
  return Array.from(new Set(out));
}

function choicesFrom(
  ref: ReferenceData,
  trigger: Trigger,
  choices: string[]
): AssignChoice[] {
  const roles = aboutRoles(ref, trigger);
  const parentsOnly = roles.length === 1 && roles[0] === "parent";
  const out = choices.map((choice): AssignChoice | null => {
    switch (choice) {
      case "student":
      case "tutor_self":
        return "self";
      case "parents":
        return parentsOnly ? "self" : "parents";
      case "finisher":
      case "filler":
        return "event_person";
      case "teach":
        return "all_tutors";
      case "tutors":
      case "task_assignees":
      case "admins":
        return choice;
      default:
        return null;
    }
  });
  return Array.from(new Set(out.filter((c): c is AssignChoice => c !== null)));
}

function assignOf(ref: ReferenceData, trigger: Trigger, a: any): AssignTo {
  return {
    choices: choicesFrom(ref, trigger, a?.choices ?? []),
    people: (a?.people ?? [])
      .map((key: string) => personFrom(ref, key))
      .filter((key: PersonKey | null): key is PersonKey => key !== null),
    tutorSubject: a?.subject ?? null,
    teachSubject: a?.teach_subject ?? null,
    share: a?.share === "each" || a?.share === "turns" ? a.share : "all",
  };
}

// ── Steps, read ───────────────────────────────────────────────────────────

const COLUMN_KEY = /^(\d+):(\d+)$/;

function columnIds(key: unknown) {
  const found = COLUMN_KEY.exec(String(key ?? ""));
  return found
    ? { boardId: Number(found[1]), columnId: Number(found[2]) }
    : { boardId: null, columnId: null };
}

function valuesOf(values: any): CrmValue[] {
  return (Array.isArray(values) ? values : [])
    .filter((v) => v && typeof v.field === "string")
    .map((v) => ({ field: v.field, value: v.value ?? null }));
}

// ── Close a task, Assign a tutor, Add a note ──────────────────────────────

/** The three actions as the backend names them (kinds' BACKEND_ACTIONS). */
const MORE_ACTIONS: Record<MoreAction, string> = {
  close_task: BACKEND_ACTIONS.close_task,
  assign_tutor: BACKEND_ACTIONS.assign_tutor,
  add_note: BACKEND_ACTIONS.add_note,
};

type MoreAction = "close_task" | "assign_tutor" | "add_note";
type MoreStep = Extract<Step, { action: MoreAction }>;

function isMoreStep(step: Step): step is MoreStep {
  return step.action in MORE_ACTIONS;
}

/** A step's own id as the backend knows it (a saved step's number), or a new step's ref. */
function stepTarget(stepId: string | null) {
  if (stepId === null) return null;
  return /^\d+$/.test(stepId) ? Number(stepId) : stepId;
}

/** One of the three, read; null for any other action. */
function moreStepOf(
  ref: ReferenceData,
  base: Pick<Step, "id" | "position" | "askFirst">,
  action: string,
  config: Record<string, any>
): Step | null {
  switch (action) {
    case MORE_ACTIONS.close_task:
      return {
        ...base,
        action: "close_task",
        closeTask: {
          stepId:
            config.step !== undefined && config.step !== null
              ? String(config.step)
              : null,
          status:
            config.status === TaskStatus.CANCELLED
              ? TaskStatus.CANCELLED
              : TaskStatus.COMPLETE,
        },
      };
    case MORE_ACTIONS.assign_tutor:
      return {
        ...base,
        action: "assign_tutor",
        assignTutor: {
          choose:
            config.choose === "subject" || config.choose === "specific"
              ? config.choose
              : "all",
          subject:
            config.subject === undefined || config.subject === null
              ? null
              : Number(config.subject),
          tutors: (Array.isArray(config.tutors) ? config.tutors : [])
            .map((key: string) => personFrom(ref, key))
            .filter((key: PersonKey | null): key is PersonKey => key !== null),
          share: config.share === "all" ? "all" : "turns",
        },
      };
    case MORE_ACTIONS.add_note:
      return {
        ...base,
        action: "add_note",
        note: {
          text: typeof config.text === "string" ? config.text : "",
          pinned: !!config.pinned,
        },
      };
    default:
      return null;
  }
}

/** One of the three, as Save takes it. */
function moreDraftOf(
  step: MoreStep,
  common: Omit<StepDraft, "action">
): StepDraft {
  const action = MORE_ACTIONS[step.action] as string as StepDraft.action;
  switch (step.action) {
    case "close_task":
      return {
        ...common,
        action,
        config: {
          step: stepTarget(step.closeTask.stepId),
          status: step.closeTask.status,
        },
      };
    case "assign_tutor": {
      const config = step.assignTutor;
      return {
        ...common,
        action,
        config: {
          choose: config.choose,
          subject: config.choose === "subject" ? config.subject : null,
          tutors:
            config.choose === "specific" ? config.tutors.map(personTo) : [],
          share: config.share,
        },
      };
    }
    case "add_note":
      return {
        ...common,
        action,
        config: { text: step.note.text, pinned: step.note.pinned },
      };
  }
}

/*
 * Their parts (contract-r3-actions.md, section 6), the same in Done's
 * details, a card and a preview's sample, as the routes send them; the
 * generated client may not have them yet.
 */
interface ApiClosedTask {
  id?: number | null;
  name?: string | null;
  before: string;
  after: string;
  assignees?: ApiPerson[];
}
interface ApiParts {
  close?: {
    step_id?: number | null;
    status: string;
    tasks?: ApiClosedTask[];
    already?: ApiClosedTask[];
  } | null;
  tutors?: {
    share: string;
    students?: {
      student: ApiPerson;
      assigned?: ApiPerson[];
      already?: ApiPerson[];
    }[];
  } | null;
  added_note?: {
    id?: number | null;
    about?: ApiPerson | null;
    text: string;
    pinned?: boolean;
    author?: ApiPerson | null;
  } | null;
  /** A card's and a sample's: what it would do, after "it would"; why it would be skipped. */
  would?: string | null;
  skip?: string | null;
}

function partsOf(from: unknown): ApiParts {
  return (from ?? {}) as ApiParts;
}

function closeStatus(status: unknown): CloseStatus {
  return status === TaskStatus.CANCELLED
    ? TaskStatus.CANCELLED
    : TaskStatus.COMPLETE;
}

function closedTasksOf(ref: ReferenceData, tasks: ApiClosedTask[] | undefined) {
  return (tasks ?? []).map(
    (task): ClosedTask => ({
      id: idOrNull(task.id),
      name: task.name ?? null,
      before: task.before as TaskStatus,
      after: task.after as TaskStatus,
      assignees: namedAll(ref, task.assignees),
    })
  );
}

/** The three actions' parts as the screens have them. */
function partsIn(
  ref: ReferenceData,
  parts: ApiParts
): Pick<ActivityDetails, "close" | "tutors" | "addedNote"> {
  const { close, tutors, added_note: note } = parts;
  return {
    close: close
      ? {
          stepId: idOrNull(close.step_id),
          status: closeStatus(close.status),
          tasks: closedTasksOf(ref, close.tasks),
          already: closedTasksOf(ref, close.already),
        }
      : null,
    tutors: tutors
      ? {
          share: tutors.share === "all" ? "all" : "turns",
          students: (tutors.students ?? []).flatMap((s): StudentTutors[] => {
            const student = named(ref, s.student);
            return student
              ? [
                  {
                    student,
                    assigned: namedAll(ref, s.assigned),
                    already: namedAll(ref, s.already),
                  },
                ]
              : [];
          }),
        }
      : null,
    addedNote: note
      ? {
          id: idOrNull(note.id),
          about: named(ref, note.about),
          text: note.text ?? "",
          pinned: !!note.pinned,
          author: named(ref, note.author),
        }
      : null,
  };
}

function stepOf(
  ref: ReferenceData,
  trigger: Trigger,
  s: StepResponse,
  position: Position
): Step {
  const base = {
    id: String(s.id),
    position,
    askFirst: {
      on: s.ask_first,
      approvers: APPROVERS_IN[s.approver_roles] ?? "admins",
    },
  };
  const config: Record<string, any> = s.config ?? {};
  const more = moreStepOf(ref, base, s.action, config);
  if (more) return more;
  switch (s.action) {
    case StepResponse.action.SEND:
      return {
        ...base,
        action: "message",
        message: {
          templateId: s.template_id ?? null,
          sendTo: (s.send_to?.length ? s.send_to : ["students"]) as SendTo[],
          sms: !!s.sms,
          email: !!s.email,
        },
      };
    case StepResponse.action.CRM_MOVE:
      return {
        ...base,
        action: "crm_move",
        crmMove: {
          ...columnIds(config.column),
          values: valuesOf(config.values),
        },
      };
    case StepResponse.action.CRM_FIELD: {
      const first = (Array.isArray(config.values) ? config.values : [])[0];
      return {
        ...base,
        action: "crm_field",
        crmField:
          first?.field === "subjects"
            ? {
                field: "subjects",
                mode: first.mode === "remove" ? "remove" : "add",
                subject_ids: (Array.isArray(first.value)
                  ? first.value
                  : []
                ).map(Number),
              }
            : { field: first?.field ?? null, value: first?.value ?? null },
      };
    }
    default:
      return {
        ...base,
        action: "task",
        task: {
          name: config.name ?? "",
          description: config.content ?? "",
          due: {
            n: Number(config.due?.n ?? 0),
            unit: config.due?.unit === "weekdays" ? "weekdays" : "days",
          },
          about: config.about === "none" ? "none" : "event",
          assignTo: assignOf(ref, trigger, config.assign),
        },
      };
  }
}

/**
 * The backend's stages as columns: each step keeps its stage. Stage 0's
 * times count from the trigger's moment (negative: before it; behind two
 * or more cards, a delay after them); a wait is a gate, its conditions its
 * cards, and its stage's steps its own, as it passes ("Right after"); any
 * other later stage's time is its gap after the column before it ("Right
 * after", "2 days later").
 */
function stepsOf(
  ref: ReferenceData,
  trigger: Trigger,
  steps: StepResponse[]
): { steps: Step[]; gates: Gate[] } {
  const gates: Gate[] = steps
    .filter((s) => s.action === StepResponse.action.WAIT)
    .map((s) => ({
      id: String(s.id),
      stage: s.stage,
      logic: s.config?.logic === "or" ? "or" : "and",
      cards: (s.config?.conditions ?? []).map((c: any) => gateCardOf(ref, c)),
    }));
  const waited = new Set(gates.map((g) => g.stage));
  return {
    steps: steps
      .filter((s) => s.action !== StepResponse.action.WAIT)
      .map((s) => {
        const position: Position = waited.has(s.stage)
          ? { ...RIGHT_AWAY }
          : timeOf(s.offset_minutes);
        return { ...stepOf(ref, trigger, s, position), stage: s.stage };
      }),
    gates,
  };
}

// ── Steps, written ────────────────────────────────────────────────────────

function empty(value: FieldValue) {
  return (
    value === null ||
    value === undefined ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  );
}

function stepDraft(
  ref: ReferenceData,
  trigger: Trigger,
  step: Step,
  stage: number,
  offset: number,
  id: number | null
): StepDraft {
  const common = {
    id,
    ref: id === null ? step.id : null,
    stage,
    offset_minutes: offset,
    ask_first: step.askFirst.on,
    approver_roles: APPROVERS_OUT[step.askFirst.approvers ?? "admins"],
  };
  if (isMoreStep(step)) return moreDraftOf(step, common);
  switch (step.action) {
    case "message":
      return {
        ...common,
        action: StepDraft.action.SEND,
        template_id: step.message.templateId,
        send_to: step.message.sendTo,
        sms: step.message.sms,
        email: step.message.email,
      };
    case "task": {
      const { task } = step;
      return {
        ...common,
        action: StepDraft.action.TASK,
        config: {
          name: task.name,
          content: task.description,
          due: task.due,
          about: task.about,
          assign: {
            choices: choicesTo(ref, trigger, task.assignTo.choices),
            subject: task.assignTo.tutorSubject,
            teach_subject: task.assignTo.teachSubject,
            people: task.assignTo.people.map(personTo),
            share: task.assignTo.share,
          },
        },
      };
    }
    case "crm_move": {
      const { boardId, columnId } = step.crmMove;
      const column =
        boardId !== null && columnId !== null ? `${boardId}:${columnId}` : "";
      // Only the values the column's rule reads, and only those given.
      const needs = new Set(
        (columnInfo(ref, column)?.needs ?? []).map((f) => f.field)
      );
      return {
        ...common,
        action: StepDraft.action.CRM_MOVE,
        config: {
          column,
          values: (step.crmMove.values ?? []).filter(
            (v) => needs.has(v.field) && !empty(v.value)
          ),
        },
      };
    }
    case "crm_field": {
      const config = step.crmField;
      return {
        ...common,
        action: StepDraft.action.CRM_FIELD,
        config: {
          values: [
            isSubjects(config)
              ? {
                  field: "subjects",
                  mode: config.mode,
                  value: config.subject_ids,
                }
              : { field: config.field, value: config.value },
          ],
        },
      };
    }
  }
}

/**
 * The screens' lane as the backend's steps: each column is a stage,
 * numbered left to right from 0 (from 1 when the lane starts with a gate),
 * every saved step keeping its id. A gate is a wait, its cards its
 * conditions and its AND or OR its logic (stored only for OR), and its own
 * steps share its stage at 0; a saved gate keeps its id. A timed column's
 * steps share its time: from the trigger in the first column (behind two
 * or more cards, a delay after them), else the gap after the column before
 * it. A first gate is reached at the kind's own timing: as it was, or the
 * kind's default (none behind two or more cards).
 */
function draftsOf(
  ref: ReferenceData,
  automation: Lane,
  last: AutomationResponse | null
): StepDraft[] {
  const before = last?.steps ?? [];
  const known = new Set(before.map((s) => String(s.id)));
  const waitOf = new Map(
    before
      .filter((s) => s.action === StepResponse.action.WAIT)
      .map((s) => [String(s.id), s])
  );
  const idOf = (stepId: string) => (known.has(stepId) ? Number(stepId) : null);
  const stepRef = (stepId: string | null) =>
    stepId === null ? null : idOf(stepId) ?? stepId;
  const stacked = entryCards(automation).length > 1;
  const timing = kindTiming(ref, automation.trigger.kind);
  const wasFirstWait = !before.some(
    (s) => s.stage === 0 && s.action !== StepResponse.action.WAIT
  );

  const out: StepDraft[] = [];
  let stage = -1;
  laneColumns(automation.steps, automation.gates ?? []).forEach((column) => {
    const gate = column.gate;
    if (gate) {
      stage = column.index === 0 ? 1 : stage + 1;
      const saved = waitOf.get(gate.id);
      const timed = column.index === 0 && !!timing && !stacked;
      out.push({
        id: saved ? saved.id : null,
        ref: saved ? null : gate.id,
        action: WAIT,
        stage,
        offset_minutes: !timed
          ? 0
          : saved && wasFirstWait
          ? saved.offset_minutes
          : defaultTimingValue(ref, automation.trigger.kind) *
            (timing.key === "hours" ? HOUR : DAY),
        config: {
          ...(gate.logic === "or" ? { logic: "or" } : {}),
          conditions: gate.cards.map((card) => conditionOf(ref, card, stepRef)),
        },
      });
      return;
    }
    const own = column.own && column.position.n <= 0;
    if (!own) stage = column.index === 0 ? 0 : stage + 1;
    const offset = own ? 0 : minutesOf(column.position);
    column.steps.forEach((step) =>
      out.push(
        stepDraft(ref, automation.trigger, step, stage, offset, idOf(step.id))
      )
    );
  });
  return out;
}

/**
 * The entry as Save takes it, when it has or had two or more cards: every
 * card in order (a saved one by its id), each with its own timing, and
 * the AND or OR. Null: the trigger alone, as `when` says it.
 */
function entryOf(
  ref: ReferenceData,
  automation: Lane,
  last: AutomationResponse | null
) {
  const cards = entryCards(automation);
  const stacked = cards.length > 1;
  if (!stacked && (last?.triggers?.length ?? 0) < 2) return null;
  return {
    triggers: cards.map((card) => ({
      id: stacked ? card.id : null,
      ...cardWhen(ref, card.trigger),
      offset_minutes: stacked ? cardMinutes(ref, card) : null,
    })),
    logic: stacked ? automation.logic ?? "and" : ("and" as TriggerJoin),
  };
}

// ── Automations ───────────────────────────────────────────────────────────

const PILLS: ActivityPillKind[] = [
  "task_created",
  "message_sent",
  "column_moved",
  "field_updated",
  "task_closed",
  "tutor_assigned",
  "note_added",
  "skipped",
  "failed",
];

/** A backend pill as the screens' kind: Not now, Stopped and Cleared are kinds of Skipped. */
function pillOf(pill: string): ActivityPillKind | null {
  if ((PILLS as string[]).includes(pill)) return pill as ActivityPillKind;
  if (["stopped", "not_now", "cleared"].includes(pill)) return "skipped";
  return null;
}

function automationOf(ref: ReferenceData, a: AutomationResponse): Automation {
  const cards: TriggerCard[] =
    (a.triggers?.length ?? 0) > 1
      ? (a.triggers ?? []).map((card) => ({
          id: card.id ?? null,
          trigger: triggerOf(ref, { kind: card.kind, options: card.options }),
          time: cardTimeOf(card.offset_minutes),
        }))
      : [];
  const trigger = cards[0]?.trigger ?? triggerOf(ref, a.when);
  const lane = stepsOf(ref, trigger, a.steps);
  const last30 = Object.fromEntries(PILLS.map((p) => [p, 0])) as Record<
    ActivityPillKind,
    number
  >;
  Object.entries(a.stats.last_30 ?? {}).forEach(([key, count]) => {
    const pill = pillOf(key);
    if (pill) last30[pill] += count;
  });
  const steps: Record<string, StepTally> = {};
  a.steps.forEach((s) => {
    steps[String(s.id)] = {
      done: s.counts.done ?? 0,
      skipped: s.counts.skipped ?? 0,
      failed: s.counts.failed ?? 0,
    };
  });
  const scheduled = a.stats.scheduled ?? 0;
  const parked = a.stats.parked ?? 0;
  return {
    id: String(a.id),
    name: a.name,
    on: a.on,
    trigger,
    triggers: cards,
    logic: a.logic === "or" ? "or" : "and",
    steps: lane.steps,
    gates: lane.gates,
    createdAt: a.created_at,
    updatedAt: a.modified_at,
    lastError: a.last_error ?? null,
    stats: {
      last30,
      waiting: a.stats.waiting ?? 0,
      upcoming: scheduled + parked,
      scheduled,
      parked,
      firstPart: a.stats.first_part ?? 0,
      steps,
      columns: (a.columns ?? []).map((c) => ({
        stepIds: c.step_ids.map(String),
        scheduled: c.counts.scheduled ?? 0,
        waiting: c.counts.waiting ?? 0,
        here: c.counts.here ?? 0,
      })),
    },
  };
}

// ── Cards ─────────────────────────────────────────────────────────────────

const ACTIONS: Record<string, ApprovalItem["action"]> = {
  send: "message",
  task: "task",
  crm_move: "crm_move",
  crm_field: "crm_field",
  [MORE_ACTIONS.close_task]: "close_task",
  [MORE_ACTIONS.assign_tutor]: "assign_tutor",
  [MORE_ACTIONS.add_note]: "add_note",
};

function proposalOf(
  ref: ReferenceData,
  card: AutomationCard,
  about: PersonKey | null
): Proposal {
  const action = ACTIONS[card.action] ?? "task";
  if (action === "message")
    return {
      action,
      templateId: card.message?.template_id ?? null,
      templateName:
        card.message?.template_name ?? card.automation_name ?? "A message",
      // One card for all the admins names them all.
      to: namedAll(
        ref,
        card.message?.recipients?.length
          ? card.message.recipients
          : card.message?.to
          ? [card.message.to]
          : []
      ),
      sms: !!card.message?.by_sms,
      email: !!card.message?.by_email,
    };
  if (action === "crm_move") {
    const { boardId, columnId } = columnIds(card.crm?.column_key);
    return {
      action,
      person: about as PersonKey,
      boardId: boardId ?? 0,
      columnId: columnId ?? 0,
      columnLabel: card.crm?.column ?? "a CRM column",
      values: valuesOf(card.crm?.values),
    };
  }
  if (action === "crm_field") {
    const first = card.crm?.values?.[0];
    const subjects = first?.field === "subjects";
    const value: FieldValue = first?.value ?? null;
    return {
      action,
      person: about as PersonKey,
      field: first?.field ?? "",
      fieldLabel: first?.label ?? "a CRM field",
      value,
      valueLabel: subjects
        ? subjectNames(ref, Array.isArray(value) ? value.map(Number) : [])
        : fieldValueWords(ref, fieldByRef(ref, first?.field), value),
      mode: subjects ? first?.mode ?? "add" : undefined,
    };
  }
  const parts = partsOf(card);
  const words = { would: parts.would ?? null, skip: parts.skip ?? null };
  if (action === "close_task") {
    const { close } = partsIn(ref, parts);
    return {
      action,
      status: close?.status ?? TaskStatus.COMPLETE,
      tasks: close?.tasks ?? [],
      ...words,
    };
  }
  if (action === "assign_tutor") {
    const students = partsIn(ref, parts).tutors?.students ?? [];
    return {
      action,
      tutors: Array.from(new Set(students.flatMap((s) => s.assigned))),
      students,
      ...words,
    };
  }
  if (action === "add_note") {
    const note = partsIn(ref, parts).addedNote;
    return {
      action,
      person: note?.about ?? about,
      text: note?.text ?? "",
      pinned: !!note?.pinned,
      ...words,
    };
  }
  const task = card.task;
  return {
    action: "task",
    name: task?.name ?? card.label,
    description: task?.content ?? "",
    dueDate: task?.due_date ?? card.created_at,
    assignees: keysWithNames(ref, task?.assignee_keys, task?.assignees),
    about: keysWithNames(ref, task?.about_keys, task?.about),
    fallback: !!task?.fallback,
  };
}

/** A card as the screens' item waiting for approval. */
function itemOf(ref: ReferenceData, card: AutomationCard): ApprovalItem {
  const role = card.person_type ? ROLE_OF_ACCOUNT_ROLE[card.person_type] : null;
  const about =
    role && card.person_id !== null && card.person_id !== undefined
      ? named(ref, {
          key: `${role}:${card.person_id}`,
          name: card.person_name ?? "",
        })
      : null;
  const asked = namedAll(ref, card.asked);
  return {
    id: card.id,
    automationId:
      card.automation_id !== null && card.automation_id !== undefined
        ? String(card.automation_id)
        : "",
    automationName: card.automation_name ?? "An automation that was deleted",
    stepId:
      card.step_id !== null && card.step_id !== undefined
        ? String(card.step_id)
        : "",
    runId: card.run_key ?? card.id,
    action: ACTIONS[card.action] ?? "task",
    approvers: APPROVERS_IN[card.approver_roles ?? "admin"] ?? "admins",
    startedBy: named(ref, card.actor),
    askedOf: Array.from(new Set([...ref.admins, ...asked])),
    askedAt: card.created_at,
    reason: card.cause ?? "",
    about,
    proposal: proposalOf(ref, card, about),
    // Why the three would be skipped now, when nothing else is said.
    note: card.note ?? partsOf(card).skip ?? null,
    preview: previewOf(ref, card),
  };
}

/**
 * What approving a card would do now, in Done's parts: the route's
 * preview, with the three actions' own parts from the card when the
 * preview has none.
 */
function previewOf(
  ref: ReferenceData,
  card: AutomationCard
): ActivityDetails | null {
  const preview = card.preview ? detailsOf(ref, card.preview) : null;
  const own = partsIn(ref, partsOf(card));
  if (!own.close && !own.tutors && !own.addedNote) return preview;
  const shown = preview ?? detailsOf(ref, undefined);
  return {
    ...shown,
    close: shown.close ?? own.close,
    tutors: shown.tutors ?? own.tutors,
    addedNote: shown.addedNote ?? own.addedNote,
  };
}

/** Change it first's changes, as the backend takes them for a task or CRM card. */
function editsTo(
  item: ApprovalItem | undefined,
  edits: ProposalEdits
): Record<string, unknown> {
  const e = edits as Record<string, any>;
  const out: Record<string, unknown> = {};
  switch (item?.proposal.action) {
    case "task":
      if (e.name !== undefined) out.name = e.name;
      if (e.description !== undefined) out.content = e.description;
      if (e.dueDate !== undefined) out.due_date = e.dueDate;
      if (e.assignees !== undefined) out.assigned = e.assignees.map(personTo);
      if (e.about !== undefined) out.about = e.about.map(personTo);
      return out;
    case "crm_move":
      if (
        e.boardId !== undefined ||
        e.columnId !== undefined ||
        e.values !== undefined
      ) {
        const p = item.proposal;
        out.column = `${e.boardId ?? p.boardId}:${e.columnId ?? p.columnId}`;
        out.values = ((e.values ?? p.values ?? []) as CrmValue[]).filter(
          (v) => !empty(v.value)
        );
      }
      return out;
    case "crm_field": {
      const p = item.proposal;
      if (e.field === undefined && e.value === undefined && !e.mode) return out;
      const field = e.field ?? p.field;
      out.values = [
        field === "subjects"
          ? {
              field,
              mode: e.mode ?? p.mode ?? "add",
              value: e.value ?? p.value,
            }
          : { field, value: e.value ?? p.value },
      ];
      return out;
    }
    case "close_task":
      if (e.status !== undefined) out.status = e.status;
      return out;
    case "assign_tutor":
      if (e.tutors !== undefined)
        out.tutors = (e.tutors as PersonKey[]).map(personTo);
      return out;
    case "add_note":
      if (e.text !== undefined || e.pinned !== undefined) {
        out.text = e.text ?? item.proposal.text;
        out.pinned = e.pinned ?? item.proposal.pinned;
      }
      return out;
    default:
      return out;
  }
}

/** What an answer did, for its toast. */
function answeredWords(
  ref: ReferenceData,
  item: ApprovalItem | undefined,
  outcome: CardOutcome,
  edits?: ProposalEdits
) {
  if (outcome.outcome === CardOutcome.outcome.SKIPPED)
    return outcome.message || "It no longer applies, so it was skipped.";
  if (outcome.outcome === CardOutcome.outcome.SCHEDULED)
    return "Approved. It happens when it is due.";
  const p = item?.proposal;
  switch (p?.action) {
    case "task":
      return `Added “${p.name}”.`;
    case "message":
      return `${p.templateName} is on its way.`;
    case "crm_move":
      return `Moved to ${p.columnLabel}.`;
    case "crm_field":
      return `Updated ${p.fieldLabel}.`;
    case "close_task": {
      const e = (edits ?? {}) as Partial<typeof p>;
      const name = p.tasks[0]?.name;
      if (!name) return "Done.";
      return (e.status ?? p.status) === TaskStatus.CANCELLED
        ? `Canceled “${name}”.`
        : `Marked “${name}” done.`;
    }
    case "assign_tutor": {
      const e = (edits ?? {}) as Partial<typeof p>;
      const tutors = e.tutors ?? p.tutors;
      return tutors.length
        ? `Assigned ${namesInWords(tutors.map((key) => nameOf(ref, key)))}.`
        : "Done.";
    }
    case "add_note":
      return p.person
        ? `Added a note to ${nameOf(ref, p.person)}'s record.`
        : "Added the note.";
    default:
      return "Done.";
  }
}

// ── Activity ──────────────────────────────────────────────────────────────

function idOrNull(id: number | null | undefined) {
  return id === null || id === undefined ? null : String(id);
}

/** The backend's ways a row is skipped, which are all Skipped pills. */
const SKIPS: SkipKind[] = ["skipped", "not_now", "cleared", "stopped"];

/** Each action as Activity's table filters and counts it (a message is send). */
const TABLE_ACTIONS: Record<ActivityAction, string> = {
  message: "send",
  task: "task",
  crm_move: "crm_move",
  crm_field: "crm_field",
  close_task: MORE_ACTIONS.close_task,
  assign_tutor: MORE_ACTIONS.assign_tutor,
  add_note: MORE_ACTIONS.add_note,
  wait: "wait",
};

/** The route's action filter, as the generated client types it. */
type TableAction = "send" | "task" | "crm_move" | "crm_field" | "wait";

/** A row's details as the screens have them; none until the backend sends them. */
function detailsOf(
  ref: ReferenceData,
  details: ApiActivityDetails | undefined
): ActivityDetails {
  const message = details?.message;
  const move = details?.move;
  return {
    message: message
      ? {
          channels: message.channels ?? [],
          to: namedAll(ref, message.recipients),
          sent: (message.sent ?? []).map((sent) => ({
            channel:
              sent.channel === ActivitySentMessage.channel.SMS
                ? "sms"
                : "email",
            subject: sent.subject ?? null,
            body: sent.body,
          })),
        }
      : null,
    tasks: (details?.tasks ?? []).map((task) => ({
      id: idOrNull(task.id),
      name: task.name ?? null,
      dueDate: task.due_date ?? null,
      assignees: namedAll(ref, task.assignees),
    })),
    move: move
      ? {
          from: move.from_columns ?? null,
          to: move.to_column ?? null,
          already: !!move.already,
        }
      : null,
    fields: (details?.fields ?? []).map((field) => ({
      label: field.label,
      before: field.before ?? null,
      after: field.after ?? null,
    })),
    note: details?.note ?? null,
    ...partsIn(ref, partsOf(details)),
  };
}

function rowOf(ref: ReferenceData, row: ApiActivityRow): ActivityRow {
  return {
    id: row.id,
    at: row.at,
    pill: pillOf(row.pill) ?? "skipped",
    action: row.action === WAIT ? "wait" : ACTIONS[row.action] ?? "task",
    skip: SKIPS.find((skip) => skip === row.pill) ?? null,
    pillLabel: row.pill_label,
    automationId: idOrNull(row.automation_id),
    automationName: row.automation_name ?? "An automation that was deleted",
    stepId: idOrNull(row.step_id),
    runId: row.run_key ?? null,
    title: row.title,
    detail: row.detail ?? "",
    cause: row.cause ?? "",
    about: named(ref, row.person),
    people: namedAll(ref, row.people),
    decidedBy: named(ref, row.decided_by),
    taskId: idOrNull(row.task_ids?.[0]),
    details: detailsOf(ref, row.details),
  };
}

function upcomingOf(ref: ReferenceData, row: ApiActivityRow): UpcomingItem {
  return {
    id: row.id,
    automationId: idOrNull(row.automation_id) ?? "",
    automationName: row.automation_name ?? "An automation that was deleted",
    stepId: idOrNull(row.step_id) ?? "",
    runId: row.run_key ?? row.id,
    about: named(ref, row.person),
    kind: row.pill === "waiting_here" ? "waiting" : "scheduled",
    dueAt: row.due_at ?? null,
    why: row.detail || row.title,
    cause: row.cause ?? "",
  };
}

// ── Connected apps ────────────────────────────────────────────────────────

function appsOf(ref: ReferenceData, list: ApiConnectedApp[]): ConnectedApps {
  const blank = (): ConnectedApp => ({
    connected: false,
    connectedBy: null,
    connectedAt: null,
    off: null,
    link: null,
    clientId: null,
    lastUsedAt: null,
    users: [],
  });
  const apps = { claude: blank(), chatgpt: blank(), zapier: blank() };
  list.forEach((a) => {
    if (!(a.app in apps)) return;
    apps[a.app as keyof typeof apps] = {
      connected: a.connected,
      connectedBy: named(ref, a.connected_by),
      connectedAt: a.connected_at ?? null,
      off: a.off ? { by: named(ref, a.off.by), at: a.off.at } : null,
      link: a.link ?? null,
      clientId: a.client_id ?? null,
      lastUsedAt: a.last_used_at ?? null,
      users: (a.users ?? []).map((use) => ({
        who: named(ref, use.who),
        name: use.who.name,
        firstAt: use.first_at,
        lastAt: use.last_at,
        calls: use.calls,
      })),
    };
  });
  return apps;
}

// ── The API ───────────────────────────────────────────────────────────────

/** A refusal in the backend's shape, so errorText shows its words. */
function refusal(message: string) {
  return Object.assign(new Error(message), { body: { detail: message } });
}

function notFound(e: unknown) {
  return e instanceof ApiError && e.status === 404;
}

export class BackendAutomationsApi implements AutomationsApi {
  private listeners = new Set<() => void>();
  /** Each automation as last read: what a save merges into. */
  private read = new Map<string, AutomationResponse>();
  /** Each card as last read: its words for an answer. */
  private items = new Map<string, ApprovalItem>();
  /** Reads under way, so the screens' simultaneous reads ask once. */
  private reading = new Map<string, Promise<unknown>>();

  constructor(private readonly ref: ReferenceData) {}

  private get orgId() {
    return this.ref.orgId;
  }

  private once<T>(key: string, ask: () => Promise<T>): Promise<T> {
    const going = this.reading.get(key) as Promise<T> | undefined;
    if (going) return going;
    const asked: Promise<T> = ask().finally(() => {
      if (this.reading.get(key) === asked) this.reading.delete(key);
    });
    this.reading.set(key, asked);
    return asked;
  }

  /** Tells the subscribers; the reads they start then are new ones, never one begun before the change. */
  private emit() {
    this.reading.clear();
    this.listeners.forEach((listener) => listener());
  }

  /** Back in view: read again, for what changed meanwhile (a task, another tab). */
  private onShow = () => {
    if (document.visibilityState === "visible") this.emit();
  };

  subscribe(listener: () => void) {
    this.listeners.add(listener);
    if (this.listeners.size === 1)
      document.addEventListener("visibilitychange", this.onShow);
    return () => {
      this.listeners.delete(listener);
      if (!this.listeners.size)
        document.removeEventListener("visibilitychange", this.onShow);
    };
  }

  private keep(a: AutomationResponse) {
    this.read.set(String(a.id), a);
    return automationOf(this.ref, a);
  }

  private keepItem(card: AutomationCard) {
    const item = itemOf(this.ref, card);
    this.items.set(item.id, item);
    return item;
  }

  // ── Automations ─────────────────────────────────────────────────────────

  async listAutomations() {
    const found = await this.once("automations", () =>
      AutomationsService.getAutomations({ orgId: this.orgId })
    );
    return found.map((a) => this.keep(a));
  }

  async getAutomation(id: string) {
    if (!/^\d+$/.test(id)) return null;
    try {
      const found = await this.once(`automation:${id}`, () =>
        AutomationsService.getAutomation({ automationId: Number(id) })
      );
      return this.keep(found);
    } catch (e) {
      if (!notFound(e)) throw e;
      this.read.delete(id);
      return null;
    }
  }

  /** An automation as the save routes take it: its name, its When (and its cards) and its steps. */
  private draftBody(
    automation: Lane & { name: string },
    last: AutomationResponse | null
  ) {
    return {
      name: automation.name.trim().slice(0, NAME_LENGTH),
      when: whenOf(automation.trigger),
      ...(entryOf(this.ref, automation, last) ?? {}),
      steps: draftsOf(this.ref, automation, last),
    };
  }

  /** What saving would count (seen), for a save that ends up on without a confirm. */
  private async seenFor(draft: AutomationSave, id: string | null) {
    const preview = await AutomationsService.previewAutomation({
      orgId: this.orgId,
      requestBody: {
        automation_id: id === null ? null : Number(id),
        draft: {
          name: draft.name,
          when: draft.when,
          triggers: draft.triggers,
          logic: draft.logic,
          steps: draft.steps,
        },
        on: true,
      },
    });
    return preview.seen;
  }

  private async lastRead(id: string) {
    return (
      this.read.get(id) ??
      (await AutomationsService.getAutomation({ automationId: Number(id) }))
    );
  }

  async createAutomation(draft: AutomationDraft, turnOn?: TurnOn) {
    const next = fitToTrigger(this.ref, draft);
    const body: AutomationSave = {
      ...this.draftBody(next, null),
      on: !!draft.on,
    };
    if (body.on) {
      body.seen = turnOn?.seen ?? (await this.seenFor(body, null));
      body.include_ready = !!turnOn?.includeReady;
    }
    const made = await AutomationsService.createAutomation({
      orgId: this.orgId,
      requestBody: body,
    });
    this.emit();
    return this.keep(made);
  }

  async updateAutomation(id: string, patch: AutomationPatch, turnOn?: TurnOn) {
    const last = await this.lastRead(id);
    const current = automationOf(this.ref, last);
    const next = fitToTrigger(this.ref, {
      name: patch.name ?? current.name,
      trigger: patch.trigger ?? current.trigger,
      triggers: patch.triggers ?? current.triggers,
      logic: patch.logic ?? current.logic,
      steps: patch.steps ?? current.steps,
      gates: patch.gates ?? current.gates,
    });
    const body: AutomationSave = {
      ...this.draftBody(next, last),
      on: patch.on ?? current.on,
    };
    if (body.on) {
      body.seen = turnOn?.seen ?? (await this.seenFor(body, id));
      body.include_ready = !!turnOn?.includeReady;
    }
    const saved = await AutomationsService.saveAutomation({
      automationId: Number(id),
      requestBody: body,
    });
    this.emit();
    return this.keep(saved);
  }

  async toggleAutomation(id: string, on: boolean, turnOn?: TurnOn) {
    const automationId = Number(id);
    let saved: AutomationResponse;
    if (on) {
      const seen =
        turnOn?.seen ??
        (
          await AutomationsService.previewAutomation({
            orgId: this.orgId,
            requestBody: { automation_id: automationId, on: true },
          })
        ).seen;
      saved = await AutomationsService.enableAutomation({
        automationId,
        requestBody: { seen, include_ready: !!turnOn?.includeReady },
      });
    } else saved = await AutomationsService.disableAutomation({ automationId });
    this.emit();
    return this.keep(saved);
  }

  async sampleStep(
    target: { id?: string | null; draft: AutomationDraft },
    stepId: string
  ): Promise<StepSample | null> {
    const last = target.id ? await this.lastRead(target.id) : null;
    const next = fitToTrigger(this.ref, {
      ...target.draft,
      name: target.draft.name || suggestName(this.ref, target.draft),
    });
    const preview = await AutomationsService.previewAutomation({
      orgId: this.orgId,
      requestBody: {
        automation_id: target.id ? Number(target.id) : null,
        draft: this.draftBody(next, last),
        on: false,
      },
    });
    const shown = preview.steps.find(
      (s) =>
        (s.id !== null && s.id !== undefined && String(s.id) === stepId) ||
        s.ref === stepId
    );
    if (!shown?.sample) return null;
    const { would, skip } = partsOf(shown.sample);
    if (!would && !skip) return null;
    return {
      person: shown.sample.person?.name || null,
      would: would ?? null,
      skip: skip ?? null,
    };
  }

  /** Who a preview names, as the screens' keys: an admin is a login there. */
  private namedPeople(people: ApiPerson[] | null | undefined) {
    return (people ?? []).map(({ key, name }) => ({
      key: personFrom(this.ref, key) ?? key,
      name,
    })) as TurnOnPeople["named"];
  }

  async previewTurnOn(target: TurnOnTarget): Promise<TurnOnPreview> {
    const last =
      target.id && target.draft ? await this.lastRead(target.id) : null;
    const draft = target.draft
      ? this.draftBody(fitToTrigger(this.ref, target.draft), last)
      : null;
    const preview = await AutomationsService.previewAutomation({
      orgId: this.orgId,
      requestBody: {
        automation_id: target.id ? Number(target.id) : null,
        draft,
        on: true,
      },
    });
    const kind =
      target.draft?.trigger.kind ??
      (target.id ? this.read.get(target.id)?.when.kind : undefined);
    return {
      seen: { reach: preview.seen.reach, ready: preview.seen.ready ?? null },
      people: {
        count: preview.reach_count ?? 0,
        named: this.namedPeople(preview.reach_people),
      },
      ready: {
        count: preview.ready_count ?? 0,
        named: this.namedPeople(preview.ready_people),
      },
      schedule: kind === "cadence",
    };
  }

  async duplicateAutomation(id: string) {
    const copy = await AutomationsService.duplicateAutomation({
      automationId: Number(id),
    });
    this.emit();
    return this.keep(copy);
  }

  async deleteAutomation(id: string) {
    await AutomationsService.deleteAutomation({ automationId: Number(id) });
    this.read.delete(id);
    this.emit();
  }

  // ── Waiting for approval, Done, Coming up ───────────────────────────────

  async listApprovals(filter: ApprovalFilter = {}) {
    const cards = filter.mine
      ? await this.once("cards:mine", () =>
          AutomationsService.getAutomationCards({ orgId: this.orgId })
        )
      : (
          await this.once("cards", () =>
            AutomationsService.getAutomationActivity({
              orgId: this.orgId,
              view: ["waiting"],
            })
          )
        ).waiting ?? [];
    return cards.map((card) => this.keepItem(card));
  }

  async answerApproval(
    id: string,
    answer: ApprovalAnswer
  ): Promise<AnswerResult> {
    const item = this.items.get(id);
    const edits =
      answer.decision === "approve" && answer.edits
        ? editsTo(item, answer.edits)
        : {};
    const result = await AutomationsService.answerAutomationCards({
      orgId: this.orgId,
      // The generator types this open object as null.
      requestBody: {
        ids: [id],
        answer:
          answer.decision === "approve"
            ? CardAnswer.answer.ADD
            : CardAnswer.answer.NOT_NOW,
        ...(Object.keys(edits).length ? { edits } : {}),
      } as unknown as CardAnswer,
    });
    this.emit();
    const outcome = result.outcomes[0];
    if (!outcome || outcome.outcome === CardOutcome.outcome.FAILED)
      throw refusal(outcome?.message || "It could not be done.");
    if (outcome.outcome === CardOutcome.outcome.TAKEN)
      throw refusal("Someone already answered it, or it changed since.");
    return {
      summary: answeredWords(
        this.ref,
        item,
        outcome,
        answer.decision === "approve" ? answer.edits : undefined
      ),
    };
  }

  async listApprovalIds(query: ApprovalsQuery): Promise<ApprovalIds> {
    const found = await AutomationsService.getAutomationWaitingIds({
      orgId: this.orgId,
      action: query.actions?.length
        ? query.actions.map((action) => TABLE_ACTIONS[action] as TableAction)
        : undefined,
      q: query.search?.trim() || undefined,
      automationId:
        query.automationId && /^\d+$/.test(query.automationId)
          ? Number(query.automationId)
          : undefined,
    });
    return {
      ids: found.ids,
      kinds: (Object.keys(TABLE_ACTIONS) as ActivityAction[]).filter((action) =>
        found.actions.includes(TABLE_ACTIONS[action])
      ),
      leftOut: found.left_out,
    };
  }

  async approveAll(
    ids: string[],
    {
      onProgress,
      stopped,
    }: { onProgress: (answered: number) => void; stopped: () => boolean }
  ): Promise<ApproveAllResult> {
    const result: ApproveAllResult = {
      approved: 0,
      skipped: 0,
      failed: 0,
      taken: 0,
      problem: null,
    };
    let answered = 0;
    try {
      for (let at = 0; at < ids.length && !stopped(); at += APPROVE_BATCH) {
        const { outcomes } = await AutomationsService.answerAutomationCards({
          orgId: this.orgId,
          // The generator types this open object as null.
          requestBody: {
            ids: ids.slice(at, at + APPROVE_BATCH),
            answer: CardAnswer.answer.ADD,
          } as unknown as CardAnswer,
        });
        for (const { outcome } of outcomes) {
          if (
            outcome === CardOutcome.outcome.DONE ||
            outcome === CardOutcome.outcome.SCHEDULED
          )
            result.approved += 1;
          else if (outcome === CardOutcome.outcome.SKIPPED) result.skipped += 1;
          else if (outcome === CardOutcome.outcome.FAILED) result.failed += 1;
          else result.taken += 1;
        }
        answered += outcomes.length;
        onProgress(answered);
      }
    } catch (e) {
      result.problem = errorText(e, "A batch didn't go through.");
    } finally {
      this.emit();
    }
    return result;
  }

  async afterTaskChange(change: TaskChange) {
    const cards = change.response?.next_up ?? [];
    this.emit();
    return cards.map((card) => this.keepItem(card));
  }

  async listActivity(filter: ActivityFilter = {}) {
    const automationId = filter.automationId
      ? Number(filter.automationId)
      : undefined;
    const limit = Math.min(ACTIVITY_LIMIT, filter.limit ?? ACTIVITY_LIMIT);
    const found = await this.once(`done:${automationId ?? ""}:${limit}`, () =>
      AutomationsService.getAutomationActivity({
        orgId: this.orgId,
        view: ["done"],
        automationId,
        limit,
      })
    );
    const rows = (found.done ?? []).map((row) => rowOf(this.ref, row));
    return filter.pills?.length
      ? rows.filter((row) => filter.pills!.includes(row.pill))
      : rows;
  }

  async listActivityPage(query: ActivityQuery): Promise<ActivityPage> {
    const found = await this.once(`page:${JSON.stringify(query)}`, () =>
      AutomationsService.getAutomationActivityPage({
        orgId: this.orgId,
        view: query.view,
        action: query.actions?.length
          ? query.actions.map((action) => TABLE_ACTIONS[action] as TableAction)
          : undefined,
        q: query.search?.trim() || undefined,
        automationId:
          query.automationId && /^\d+$/.test(query.automationId)
            ? Number(query.automationId)
            : undefined,
        skip: query.page * query.pageSize,
        limit: query.pageSize,
      })
    );
    const counts = Object.fromEntries(
      (Object.keys(TABLE_ACTIONS) as ActivityAction[]).map((action) => [
        action,
        found.counts[TABLE_ACTIONS[action]] ?? 0,
      ])
    ) as Record<ActivityAction, number>;
    return {
      view: query.view,
      items: (found.waiting ?? []).map((card) => this.keepItem(card)),
      rows: (found.done ?? []).map((row) => rowOf(this.ref, row)),
      total: found.total,
      leftOut: found.left_out ?? 0,
      counts,
    };
  }

  async listUpcoming(automationId?: string) {
    const id = automationId ? Number(automationId) : undefined;
    const found = await this.once(`upcoming:${id ?? ""}`, () =>
      AutomationsService.getAutomationActivity({
        orgId: this.orgId,
        view: ["upcoming"],
        automationId: id,
        limit: ACTIVITY_LIMIT,
      })
    );
    return (found.upcoming ?? []).map((row) => upcomingOf(this.ref, row));
  }

  // ── Connected apps ──────────────────────────────────────────────────────

  async getConnectedApps() {
    const found = await this.once("apps", () =>
      AutomationsService.getConnectedApps({ orgId: this.orgId })
    );
    return appsOf(this.ref, found);
  }

  async disconnectApp(app: AppName) {
    const found = await AutomationsService.disconnectConnectedApp({
      orgId: this.orgId,
      app,
    });
    this.emit();
    return appsOf(this.ref, found);
  }

  async connectAppAgain(app: AppName) {
    const found = await AutomationsService.connectConnectedApp({
      orgId: this.orgId,
      app,
    });
    this.emit();
    return appsOf(this.ref, found);
  }
}

/** The AutomationsApi over the backend, for an organization's reference data. */
export function createBackendApi(ref: ReferenceData): AutomationsApi {
  return new BackendAutomationsApi(ref);
}
